/**
 * The first page of a PDF, as an image.
 *
 * ── Why a certificate needs this ──────────────────────────────────────────────────────────
 *
 * Certificates arrive as PDFs at least as often as photos, and a card grid that draws the photos
 * but prints `certificate.pdf` for the PDFs is showing the evidence for half the register and a
 * filename for the other half. A filename is not a certificate: the reader cannot tell an AWS
 * associate badge from a first-aid course without opening it.
 *
 * Nothing in the browser rasterises a PDF on its own — an `<img>` pointed at one renders the
 * broken-image icon — and nothing in this stack rasterises one server-side either, because S3 and
 * Amplify Storage store what they are given. So it happens here, with pdf.js, in the browser:
 * page one onto a canvas, out as a JPEG.
 *
 * ── Where the cost falls, and why that is the right place ─────────────────────────────────
 *
 * Twice, deliberately:
 *
 *   at UPLOAD    once per document, and the JPEG is stored beside the original under the same
 *                `.thumb.jpg` key the image path already uses. Every later view is then a
 *                thirty-kilobyte image — no PDF parsed, no pdf.js loaded at all.
 *   at VIEW      for everything uploaded before this existed, which has no stored thumbnail.
 *                Those download the PDF and render it once per session.
 *
 * The second path is the fallback, not the plan. It is why `pdfjs-dist` is imported dynamically: a
 * register of photographs never loads the library or the worker beside it, and a register of PDFs
 * loads them once, after the cards and their text are already on screen.
 *
 * ── Failure is always null, never a throw ─────────────────────────────────────────────────
 *
 * A password-protected PDF, a corrupt one, a browser whose canvas cannot encode JPEG, a worker
 * that 404s, a fetch the bucket CORS refuses — all of them mean "no thumbnail", which is a
 * cosmetic absence on a card that still shows the certification and still opens the document. None
 * of them is worth failing an upload or a render over, so every one returns null and says so in
 * the console.
 */

/** The longest edge of the rendered page, in pixels. Matches the image thumbnails' own bound. */
export const PDF_PAGE_IMAGE_MAX_EDGE = 640;
const PDF_PAGE_IMAGE_QUALITY = 0.72;

/**
 * pdf.js, loaded once and only when a PDF actually has to be drawn.
 *
 * The `legacy` build rather than the modern one: CRA compiles through Babel to the browserslist in
 * package.json, and the modern build ships syntax newer than that target which would reach a
 * browser untranspiled.
 *
 * The worker is a URL, not an import. A worker script is fetched by the browser rather than pulled
 * into the module graph, so it lives in `public/` — put there by scripts/copy-pdf-worker.js, which
 * is what keeps its version identical to this library's. A mismatch between the two is a hard
 * error from pdf.js rather than a degradation, which is exactly why that file is copied on install
 * rather than committed once and forgotten.
 */
let pdfjsPromise: Promise<typeof import('pdfjs-dist')> | null = null;

function loadPdfjs(): Promise<typeof import('pdfjs-dist')> {
  if (!pdfjsPromise) {
    pdfjsPromise = import('pdfjs-dist/legacy/build/pdf').then((pdfjs) => {
      // PUBLIC_URL is '' when the app is served from the domain root and a path when it is not, so
      // this follows the app rather than assuming it sits at '/'.
      pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.js`;
      return pdfjs;
    });
    // A failed load must not be cached as a permanently broken library: a worker that 404'd once
    // behind a cold CDN should be retried the next time a card needs one.
    pdfjsPromise.catch(() => {
      pdfjsPromise = null;
    });
  }
  return pdfjsPromise;
}

/** Is this file, or this stored key, a PDF? The one place the extension is spelled out. */
export function isPdfDocument(nameOrKey?: string | null): boolean {
  return /\.pdf$/i.test(String(nameOrKey || '').trim());
}

/**
 * Render page one of a PDF to a JPEG blob, or null if it cannot be done.
 *
 * Bytes are passed rather than a URL even when they came from one, because pdf.js takes ownership
 * of a `data` buffer — handing it a shared ArrayBuffer and then reading that buffer again
 * elsewhere is a detached-buffer error, so callers give this the only copy they hold.
 */
export async function renderPdfFirstPage(
  data: ArrayBuffer | Uint8Array,
  maxEdge: number = PDF_PAGE_IMAGE_MAX_EDGE
): Promise<Blob | null> {
  if (typeof document === 'undefined') return null;

  let pdf: import('pdfjs-dist').PDFDocumentProxy | null = null;
  try {
    const pdfjs = await loadPdfjs();
    pdf = await pdfjs.getDocument({
      data,
      /* Both off on purpose. This renders a page to pixels and nothing else: it does not run a
         PDF's embedded JavaScript, does not fill a form, and does not need a font server. A
         certificate is a document from outside the company, so the smaller the surface the
         better. */
      isEvalSupported: false,
      disableAutoFetch: true,
    }).promise;

    const page = await pdf.getPage(1);
    /*
     * Scale to the band rather than render at the page's own size. A PDF page is measured in
     * points — 612×792 for US Letter — which is not pixels, and `scale` is how pdf.js is asked for
     * a different resolution. A certificate is usually landscape and sometimes A4 portrait, so the
     * longest edge is what gets bounded: the same rule the image thumbnails use, so both kinds of
     * document land in the same size class.
     */
    const unscaled = page.getViewport({ scale: 1 });
    const longest = Math.max(unscaled.width, unscaled.height);
    if (!longest) return null;
    const viewport = page.getViewport({ scale: maxEdge / longest });

    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(viewport.width));
    canvas.height = Math.max(1, Math.round(viewport.height));
    const context = canvas.getContext('2d');
    if (!context) return null;
    /* A white ground. A PDF page has no background of its own — the paper is the absence of ink —
       and a JPEG has no transparency, so an unpainted canvas would flatten to black. */
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);

    await page.render({ canvasContext: context, viewport }).promise;

    return await new Promise<Blob | null>((resolve) => {
      if (typeof canvas.toBlob !== 'function') {
        resolve(null);
        return;
      }
      canvas.toBlob((blob) => resolve(blob), 'image/jpeg', PDF_PAGE_IMAGE_QUALITY);
    });
  } catch (error) {
    console.warn('[PdfPageImage] The first page could not be rendered.', error);
    return null;
  } finally {
    // Release the parsed document and the worker's copy of it. Without this, a grid of PDFs holds
    // every page it ever drew in memory for as long as the tab is open.
    try {
      await pdf?.destroy();
    } catch {
      /* Already gone, or never opened. */
    }
  }
}

/**
 * Fetch a PDF and render its first page, or null.
 *
 * A signed S3 URL, in practice. The fetch is a plain cross-origin GET — the same request the
 * `<img>` tags on the card grid make — so nothing here depends on the HEAD rule that the download
 * path has to work around.
 */
export async function renderPdfFirstPageFromUrl(
  url: string,
  maxEdge: number = PDF_PAGE_IMAGE_MAX_EDGE
): Promise<Blob | null> {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      console.warn('[PdfPageImage] The PDF could not be fetched:', response.status);
      return null;
    }
    return await renderPdfFirstPage(await response.arrayBuffer(), maxEdge);
  } catch (error) {
    console.warn('[PdfPageImage] The PDF could not be fetched.', error);
    return null;
  }
}
